import {
	Component,
	OnInit,
	ViewEncapsulation,
	Input,
	Output,
	EventEmitter,
	forwardRef,
	ViewChild,
	ElementRef,
	OnDestroy,
	SimpleChanges,
	OnChanges,
	ChangeDetectorRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { IdService, isMobile, IGenericSelectOption, FormControlBase } from '../../shared/index';
import { fromEvent, Subscription } from 'rxjs';

@Component({
	selector: 'rbc-dropdown-alt',
	templateUrl: './dropdown-alt.component.html',
	encapsulation: ViewEncapsulation.None,
	styleUrls: ['./dropdown-alt.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => DropdownAltComponent)
		}
	]
})
export class DropdownAltComponent extends FormControlBase<string> implements OnInit, OnChanges, OnDestroy {
	@Input() disabled: boolean = false;
	@Input() height: string;
	@Input() helpText: string;
	@Input() id: string;
	@Input() label: string;
	@Input() set options(options: IGenericSelectOption[]) {
		if (options && options.length) {
			const filterSelected = options.filter(option => {
				if (this.preselect || this.innerValue) {
					return option.value === this.preselect || option.value === this.innerValue;
				} else {
					return option.selected === true;
				}
			});

			this.dropdownOptions = options.map(o => {
				const { selected, ...noSelected } = o;

				return noSelected;
			});

			/*
        Using writeValue here over setModel under the presumtion that the user explicitly set the option selected
        and we don't need to inform them/angular that the model changed.

        NOTE: We should highly discourage people from doing this and instead just use normal template/reactive form model setting principles.
      */
			if (filterSelected.length > 0) {
				this.writeValue(filterSelected[0].value);
			}
		}
	}

	get options() {
		return this.dropdownOptions;
	}

	@Input() preselect: string;
	@Input() size: string;
	@Input() sizeWidth: string;
	@Input() ariaLabel: string;
	@Input() ariaLabelledby: string;
	@Input() ariaDescribedby: string;
	@Input() ariaActiveDecendant: string;

	@Output() selectUpdate = new EventEmitter<string[]>();

	@ViewChild('self') self: ElementRef;
	@ViewChild('dropdown') $dropdown: ElementRef;
	@ViewChild('cardResults') $cardResults: ElementRef;

	private dropdownOptions: IGenericSelectOption[] = [];
	loaded: boolean = false;
	selectedLabel: string;
	selected: string;
	open: boolean = false;
	highlightID: string;
	activeIndex: number = null;
	index: number = 1;

	$clickListener: Subscription;

	constructor(private idService: IdService, private eRef: ElementRef, private cdr: ChangeDetectorRef) {
		super();
		this.id = idService.generate();
	}

	ngOnInit() {
		this.initClickListener();
	}

	ngOnChanges(changes: SimpleChanges) {
		super.ngOnChanges(changes);

		if (changes && changes.preselect) {
			this.writeValue(changes.preselect.currentValue);
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
		this.idService.remove(this.id);
		this.$clickListener.unsubscribe();
	}

	classGen() {
		return {
			[`dropdown-${this.size}`]: this.size && (this.size === 'large' || this.size === 'small'),
			[`dropdown-width-${this.sizeWidth}`]: this.sizeWidth,
			disabled: this.disabled && !this.disabled
		};
	}

	isMobileHandler(): boolean {
		return isMobile();
	}

	// Handlers
	changeHandler() {
		this.selectUpdate.emit([this.innerValue]);
	}

	// remove the event listener if null prop or target is not the dropdown
	$_SelectDropdown_close() {
		this.open = false;
		this.highlightID = null;
		this.activeIndex = null;
	}

	$_SelectDropdown_open(e = null) {
		this.index = this.options.findIndex(option => option.value === this.innerValue);
		this.open = !this.open && !(this.disabled && !this.disabled);
		// if menu currently open don't add another event handler
		if (!this.open) {
			this.$_SelectDropdown_close();
			this.$_SelectDropdown_close();
		}
	}

	$_SelectDropdown_updateSelected(label, value) {
		this.setModel(value);
		this.selectUpdate.emit([value]);
		this.open = false;
	}

	$_SelectDropdown_nextEligibleOption(index, options, nbOptions) {
		let nextIndex = index + 1;
		while (nextIndex < nbOptions && options[nextIndex].disabled) {
			nextIndex += 1;
		}
		// if next index was not past end return it else return original
		return nextIndex !== nbOptions ? nextIndex : index;
	}

	$_SelectDropdown_previousEligibleOption(index, options) {
		let nextIndex = index - 1;
		while (nextIndex > -1 && options[nextIndex].disabled) {
			nextIndex -= 1;
		}
		// if next index was not past end return it else return original
		return nextIndex > -1 ? nextIndex : index;
	}

	$_SelectDropdown_updateHighlight(e) {
		const key = e.key.toLowerCase();
		const options = this.options;
		const nbOptions = options.length;
		if (nbOptions > 0 && this.open) {
			// if key press down
			if (key.indexOf('down') > -1) {
				e.preventDefault();
				this.index = this.$_SelectDropdown_nextEligibleOption(this.index, this.options, nbOptions);
				this.highlightID = options[this.index].id;
				this.activeIndex = this.index;
			} else if (key.indexOf('up') > -1) {
				e.preventDefault();
				this.index = this.$_SelectDropdown_previousEligibleOption(this.index, this.options);
				this.highlightID = options[this.index].id;
				this.activeIndex = this.index;
			} else if (key === 'enter' && !this.options[this.index].disabled) {
				e.preventDefault();
				this.$_SelectDropdown_updateSelected(this.options[this.index].label, this.options[this.index].value);
				this.$_SelectDropdown_close();
			} else if (key === 'escape' || key === ' ') {
				e.preventDefault();
				this.open = false;
				this.$_SelectDropdown_close();
			}
		} else if (key.indexOf('down') > -1 || key.indexOf('up') > -1 || key === 'enter' || key === ' ') {
			e.preventDefault();
			this.$_SelectDropdown_open();
		}
	}

	processValue() {
		if (this.options) {
			const option = this.options.find(o => o.value === this.innerValue);

			if (option) {
				this.selected = option.value;
				this.selectedLabel = option.label;
			} else {
				this.selected = null;
				this.selectedLabel = null;
			}
		}
	}

	setModel(value: string) {
		super.setModel(value);
		this.processValue();
	}

	writeValue(value: string) {
		super.writeValue(value);
		this.processValue();
	}

	initClickListener() {
		this.$clickListener = fromEvent(this.self.nativeElement, 'rbcDocumentClick').subscribe((event: CustomEvent) => {
			if (!this.eRef.nativeElement.contains(event.detail) && this.open) {
				this.$_SelectDropdown_close();
				this.cdr.markForCheck();
			}
		});
	}
}
